import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../../../components/button/button";
import RadioGroup from "../../../../../../components/radio-group/radio-group";
import Select from "../../../../../../components/select/select";
import { useLineups } from "../../../../../../hooks/use-lineups";
import { useSubstitutions } from "../../../../../../hooks/use-substitutions";
import { openXShareIntent } from "../../../../../../lib/x-share";
import { goalRequest } from "../../../../../../redux/broadcast/broadcast.actions";
import {
	selectAwayTeam,
	selectBroadcastId,
	selectFixtureId,
	selectHomeTeam,
} from "../../../../../../redux/broadcast/broadcast.selectors";
import UpdatesSocial from "../../components/social/updates-social";
import UpdatesTextArea from "../../components/updates-textarea/updates-textarea";
import useSocialShare from "../../hooks/use-social-share";
import useUpdatesText from "../../hooks/use-updates-text";

const GoalsUpdatesControls = () => {
	const dispatch = useDispatch();
	const { setUpdateText, updateText } = useUpdatesText();
	const homeTeam = useSelector(selectHomeTeam);
	const awayTeam = useSelector(selectAwayTeam);
	const fixtureId = useSelector(selectFixtureId);
	const broadcastId = useSelector(selectBroadcastId);

	const lineups = useLineups(fixtureId);
	const substitutions = useSubstitutions(broadcastId);

	const [activeTeam, setActiveTeam] = useState<"home" | "away">("home");
	const [scorerId, setScorerId] = useState("");
	const social = useSocialShare();

	const teamLineup = lineups?.[activeTeam];

	const playersOnPitch = useMemo(() => {
		if (!teamLineup) return [];

		const teamSubs = substitutions.filter((s) => s.team === activeTeam);
		const subbedOff = new Set(
			teamSubs.flatMap((s) => s.subs.map((p) => p.playerOut)),
		);
		const subbedOn = new Set(
			teamSubs.flatMap((s) => s.subs.map((p) => p.playerIn)),
		);

		return [
			...teamLineup.startXI.filter((p) => !subbedOff.has(p.name)),
			...teamLineup.substitutes.filter((p) => subbedOn.has(p.name)),
		];
	}, [teamLineup, substitutions, activeTeam]);

	const handlePostUpdate = () => {
		if (!broadcastId) return;

		const comment = updateText?.trim();
		const teamName = activeTeam === "home" ? homeTeam : awayTeam;

		dispatch(
			goalRequest({
				broadcastId,
				team: activeTeam,
				teamId: teamLineup?.teamId,
				action: "add",
				scorerId: scorerId ? Number(scorerId) : undefined,
				comment: comment || undefined,
				share: social.blueSky,
				hashtags: social.parsedHashtags,
			}),
		);

		if (social.postToX) {
			openXShareIntent(
				[`⚽ Goal for ${teamName}!`, comment].filter(Boolean).join("\n"),
				broadcastId,
				social.parsedHashtags,
			);
		}

		setUpdateText("");
		setScorerId("");
		social.reset();
	};

	return (
		<>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: "1.2rem",
				}}
			>
				<div
					style={{
						width: "50%",
						display: "flex",
						alignItems: "center",
					}}
				>
					<RadioGroup
						items={[
							{
								id: "home",
								disabled: false,
								label: homeTeam,
							},
							{
								id: "away",
								disabled: false,
								label: awayTeam,
							},
						]}
						activeId={activeTeam}
						onChange={(team) => {
							setActiveTeam(team as "home" | "away");
							setScorerId("");
						}}
					/>
				</div>
				<Select
					value={scorerId}
					onChange={setScorerId}
					placeholder="Select scorer"
					options={playersOnPitch.map((player) => ({
						value: String(player.id),
						label: `${player.name} - ${player.number}`,
					}))}
				/>
			</div>
			<UpdatesTextArea
				value={updateText}
				onChange={(value) => setUpdateText(value)}
				onSubmit={handlePostUpdate}
				rows={4}
			/>
			<div
				style={{
					display: "flex",
					alignItems: "flex-end",
				}}
			>
				<UpdatesSocial
					blueSky={social.blueSky}
					onBlueSkyChange={social.setBlueSky}
					postToX={social.postToX}
					onPostToXChange={social.setPostToX}
					hashtags={social.hashtags}
					onHashtagsChange={social.setHashtags}
				/>
				<Button onClick={handlePostUpdate}>Post update</Button>
			</div>
		</>
	);
};

export default GoalsUpdatesControls;
